import { ref, computed, watch } from 'vue'
import { isEqual } from 'lodash'
import { useConnector } from './useConnector'
import { connectInfiniteHits } from '@/helpers/instantSearchConnectors/connectInfiniteHits'

const getStateWithoutPage = ({ page, ...rest } = {}) => rest

/**
 * Bridges connectInfiniteHits to vvirtual-grid's pageProvider API.
 *
 * @param {Object} [options]
 * @param {number} [options.fallbackPerPage=40] - Fallback page size before first results arrive.
 *
 * @returns {{
 *   state: import('vue').Ref,
 *   length: import('vue').ComputedRef<number>,
 *   pageSize: import('vue').ComputedRef<number>,
 *   pageProvider: Function,
 *   scrollTo: import('vue').Ref<number>,
 *   cacheKey: import('vue').Ref<number>
 * }}
 */
export function useVirtualHitsPageProvider ({ fallbackPerPage = 40 } = {}) {
  // Our own cache refs — written to by the connector's cache callbacks.
  // Shallow-copy on every write so watchers detect page additions without deep mode.
  const cachedHits = ref(null) // { [pageIdx]: Hit[] } sparse map
  const cachedState = ref(null) // normalized helper state (no page key)

  const cache = {
    read: ({ state }) =>
      isEqual(cachedState.value, getStateWithoutPage(state)) ? cachedHits.value : null,
    write: ({ state, hits }) => {
      cachedState.value = getStateWithoutPage(state)
      cachedHits.value = { ...hits } // shallow copy → new reference → watcher fires on every write
    }
  }

  const state = useConnector(connectInfiniteHits, {
    $$widgetType: 'ais.infiniteHits',
    cache
  })

  const length = computed(() => state.value?.nbHits ?? 0)
  const pageSize = computed(() => state.value?.results?.hitsPerPage ?? fallbackPerPage)

  // Scroll-to-top + force-remount on query / refinement change.
  // cachedState changes whenever a new query or refinement produces its first result.
  const scrollTo = ref(0)
  const cacheKey = ref(Date.now())
  let prevCachedState = null

  watch(cachedState, newState => {
    if (prevCachedState !== null && !isEqual(newState, prevCachedState)) {
      scrollTo.value = 0
      cacheKey.value = Date.now()
    }
    prevCachedState = newState
  })

  // Stable function reference — never recreated. VirtualGrid memoizes on identity.
  function pageProvider (pageNumber) {
    const page = cachedHits.value?.[pageNumber]
    if (page) return Promise.resolve(page)

    // Page not cached yet — trigger fetch then await it
    state.value?.showMore([pageNumber])

    return new Promise((resolve, reject) => {
      const TIMEOUT_MS = 15_000
      const timer = setTimeout(() => {
        stop()
        reject(new Error(`[useVirtualHitsPageProvider] Page ${pageNumber} timed out after ${TIMEOUT_MS}ms`))
      }, TIMEOUT_MS)

      const stop = watch(cachedHits, newHits => {
        if (newHits?.[pageNumber]) {
          clearTimeout(timer)
          stop()
          resolve(newHits[pageNumber])
        }
      })
    })
  }

  return { state, length, pageSize, pageProvider, scrollTo, cacheKey }
}
